import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { useSnackbar } from "notistack";
import { makeStyles } from "@mui/styles";
import { htmlDecode } from "../../utils/convertHtml";
import ChatCard from "./ChatCard";
import { picBaseUrl } from "../../config/config";
import { ModelBox } from "../../components";
import {
  csvImage,
  docImage,
  pdfImage,
  wordImage,
  audioImage,
  xlsxImage,
  otherImage,
} from "../../assets";
import BasicBreadcrumbs from "../../components/GeneralComponents/BreadCrumbs";
import DeleteConfirmation from "../../components/DeleteConfirmation";
import {
  _List_Support_Ticket_Comments,
  _Mark_Resolve_Support_Ticket,
} from "../../DAL/Listing/SupportTickets";
import FileViewerModal from "./DocumentModal";
import { useAdminContext } from "../../Hooks/AdminContext";

const useStyles = makeStyles(() => ({
  loading: {
    marginLeft: "50%",
    marginTop: "20%",
  },
}));

const SupportTicketDetail = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const params = useParams();
  const { socket } = useAdminContext();
  const [loading, setLoading] = useState(false);
  const { state } = useLocation();
  const [supportTicketData, setSupportTicketData] = useState([]);
  const [openResolved, setOpenResolved] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [selectedFileUrl, setSelectedFileUrl] = useState(null);

  const imageLinks = {
    docx: wordImage,
    mp3: audioImage,
    pdf: pdfImage,
    csv: csvImage,
    doc: docImage,
    xlsx: xlsxImage,
    xls: xlsxImage,
    other: otherImage,
  };

  const handleOpen = (image_path) => {
    setImageUrl(image_path);
    setOpen(true);
  };

  const handleClose = () => {
    setImageUrl("");
    setOpen(false);
  };

  const handleMarkResolvedPopup = (value) => {
    setOpenResolved(true);
  };

  const handleNavigate = () => {
    navigate("/support-ticket", {
      state: state?.tabValue ? state?.tabValue : 0,
    });
  };

  const getSupportTicketDetail = async () => {
    setLoading(true);
    const result = await _List_Support_Ticket_Comments(params.id);

    if (result.code === 200) {
      setSupportTicketData({
        ...result.support_ticket,
        comment: result.list_support_ticket_comment,
      });
      setIsLoading(false);
      setLoading(false);
    } else {
      setLoading(false);
      enqueueSnackbar(result.message, { variant: "error" });
      navigate(`/support-ticket`);
    }
  };

  const handleMarkResolved = async () => {
    setOpenResolved(false);
    setIsLoading(true);
    const result = await _Mark_Resolve_Support_Ticket(params.id);
    if (result.code === 200) {
      socket.emit("support_ticket_event", {
        notification: result.notification,
      });
      getSupportTicketDetail(result.support_ticket);
      setIsLoading(false);
      enqueueSnackbar(result.message, { variant: "success" });
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
      navigate(`/support-ticket`);
    }
  };

  const getCommentImage = (file) => {
    const ext = file?.split(".").pop();
    if (
      ext == "jpg" ||
      ext == "JPG" ||
      ext == "png" ||
      ext == "webp" ||
      ext == "jpeg" ||
      ext == "JPEG" ||
      ext == "PNG"
    ) {
      return picBaseUrl + file;
    } else if (imageLinks[ext]) {
      return imageLinks[ext];
    } else {
      return imageLinks.other;
    }
  };

  useEffect(() => {
    getSupportTicketDetail();
  }, [params.id]);

  const BreadCrumbsList = [
    {
      title: "Support Tickets",
      navigation: "/support-ticket",
      active: false,
    },
    {
      title: `Support Tickets Detail`,
      navigation: "",
      status: "Active",
    },
  ];

  if (isLoading || loading) {
    return <CircularProgress className={classes.loading} color="primary" />;
  }

  return (
    <div className="container">
      <div className="row  mt-2">
        <div className="col-12 d-flex flex-wrap justify-content-between mb-3">
          <div className="col-12 col-md-7">
            <BasicBreadcrumbs items={BreadCrumbsList} />
          </div>
          <div className="col-12 col-md-5">
            {supportTicketData.ticket_status === 1 ? (
              <button
                className="small-contained-button float-end mt-1 disabled"
                disabled="disabled"
              >
                Resolved
              </button>
            ) : supportTicketData.ticket_status === 0 ? (
              <button
                className="small-contained-button float-end mt-1"
                onClick={handleMarkResolvedPopup}
              >
                Mark Resolved
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="row media-margin">
        <div className="col-12">
          <h5
            style={{
              overflowWrap: "anywhere",
            }}
          >
            {supportTicketData?.subject}
          </h5>
          <p
            style={{
              overflowWrap: "anywhere",
            }}
          >
            {htmlDecode(supportTicketData.description)}
          </p>
        </div>
        <div className="col-sm-12 my-2">
          {supportTicketData.ticket_images &&
            supportTicketData.ticket_images.map((image, i) => {
              const ext = image.thumbnail_1?.split(".").pop();
              if (
                ext == "jpg" ||
                ext == "JPG" ||
                ext == "png" ||
                ext == "webp" ||
                ext == "jpeg" ||
                ext == "JPEG" ||
                ext == "PNG"
              ) {
                return (
                  <span className="preview ticket_image_preview" key={i}>
                    <img
                      onClick={() => {
                        handleOpen(picBaseUrl + image);
                      }}
                      className="p-0"
                      src={picBaseUrl + image}
                    />
                  </span>
                );
              } else {
                return (
                  <span className="preview ticket_image_preview" key={i}>
                    <a
                      className="pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        setSelectedFileUrl(picBaseUrl + image);
                      }}
                    >
                      <img src={getCommentImage(image)} />
                    </a>
                  </span>
                );
              }
            })}
        </div>
        <ChatCard
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          comments={supportTicketData.comment}
          ticket_id={supportTicketData._id}
          list_data={getSupportTicketDetail}
          supportTicketData={supportTicketData}
        />
        <DeleteConfirmation
          open={openResolved}
          setOpen={setOpenResolved}
          handleClose={() => setOpenResolved(false)}
          title={"Are you sure you want to mark this ticket as resolved?"}
          handleAgree={handleMarkResolved}
          Btn_Title={"Agree"}
        />
        <FileViewerModal
          open={Boolean(selectedFileUrl)}
          onClose={() => setSelectedFileUrl(null)}
          fileUrl={selectedFileUrl}
        />
        <ModelBox open={open} handleClose={handleClose} image_url={imageUrl} />
      </div>
    </div>
  );
};

export default SupportTicketDetail;
